import { isUserSettingsLimited } from '@domains/billing-settings/priv/CustomerUserSettings/api/mappers'
import ErrorIcon from '@mui/icons-material/Error'
import { Skeleton as _Skeleton, Button, FormControlLabel, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material'
import { Controller, useForm, useWatch } from 'react-hook-form'
import type { UserPrice, UserPriceRequest, UserSettings, UserSettingsRequest } from '../api/api'
import { BorderedStack } from '../components/BorderedStack'
import { Loader } from '../components/Loader'

export interface CustomerUserSettingsProps {
  isLoading: boolean
  isPatching: boolean
  isDeleting: boolean
  price: UserPrice | undefined
  settings: UserSettings | undefined
  onSettingsChange: (settings: UserSettingsRequest) => void
  onPriceChange: (price: UserPriceRequest) => void
  onSettingsDelete: () => void
  onPriceDelete: () => void
}

type FormValues = Pick<CustomerUserSettingsProps, 'settings' | 'price'>

export const CustomerUserSettings = ({
  isLoading,
  isPatching,
  isDeleting,
  settings,
  price,
  onSettingsChange,
  onPriceChange,
  onSettingsDelete,
  onPriceDelete,
}: CustomerUserSettingsProps) => {
  const form = useForm<FormValues>({
    values: {
      settings,
      price,
    },
  })

  const formSettings = useWatch({ control: form.control, name: 'settings' })
  const showAllowedUsersField = formSettings ? isUserSettingsLimited(formSettings.limit) : false

  const handleSubmit = (data: FormValues) => {
    if (data.settings && form.formState.dirtyFields.settings) {
      onSettingsChange({
        limit: data.settings.limit,
      })
    }

    if (data.price && form.formState.dirtyFields.price) {
      onPriceChange({
        numberOfFreeUsers: data.price.numberOfFreeUsers,
        pricePerUser: data.price.pricePerUser,
        numberOfBilledUsers: data.price.numberOfBilledUsers,
      })
    }
  }

  const handleDeleteOverride = () => {
    if (settings?.isOverriden) {
      onSettingsDelete()
    }

    if (price?.isOverriden) {
      onPriceDelete()
    }
  }

  if (!isLoading && (!settings || !price)) {
    return (
      <BorderedStack
        sx={{
          flex: 1,
          padding: 2,
        }}
      >
        <Typography sx={{ color: 'red' }} variant="p2Regular">
          Failed to load user settings
        </Typography>
      </BorderedStack>
    )
  }

  const isOverriden = Boolean(settings?.isOverriden || price?.isOverriden)

  return (
    <BorderedStack
      sx={{
        flex: 1,
        gap: 2,
        justifyContent: 'space-between',
        padding: 2,
      }}
    >
      <Stack
        sx={{
          gap: 3,
        }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
          }}
        >
          <Stack
            direction="row"
            sx={{
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Stack
              sx={{
                gap: 0.5,
              }}
            >
              <Stack
                direction="row"
                sx={{
                  gap: 0.5,
                  alignItems: 'center',
                }}
              >
                <Typography variant="p2Medium">User settings</Typography>
                {isOverriden && (
                  <Tooltip placement="top" title="The values has been changed from the default settings">
                    <ErrorIcon data-testid="override-warning" fontSize="inherit" color="warning" />
                  </Tooltip>
                )}
              </Stack>
              {price && (
                <Typography data-testid="billable-users" variant="p4Regular" color="textSecondary">
                  The customer has <b>{price.numberOfBilledUsers}</b> billable users
                </Typography>
              )}
            </Stack>
          </Stack>
          <Controller
            control={form.control}
            name="settings.limit.type"
            render={({ field }) => (
              <FormControlLabel
                onChange={(_, checked) => {
                  form.setValue('settings.limit', checked ? { type: 'unlimited' } : { type: 'limited', allowedUsers: 0 }, {
                    shouldDirty: true,
                  })
                }}
                sx={{ margin: 0, alignItems: 'flex-end' }}
                labelPlacement="top"
                control={<Switch disabled={isLoading} role="switch" checked={field.value === 'unlimited'} value={field.value} />}
                label={
                  <Typography variant="p4Regular" color="textSecondary">
                    Unlimited users
                  </Typography>
                }
              />
            )}
          />
        </Stack>
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Loader isLoading={isLoading} loader={CustomerUserSettingsSkeleton}>
            <Stack
              direction="row"
              sx={{
                flex: 1,
                gap: 2,
              }}
            >
              <Controller
                control={form.control}
                name="price.numberOfFreeUsers"
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    slotProps={{ htmlInput: { 'aria-label': 'number-of-free-users' } }}
                    label="Number of free users"
                    onChange={(event) => field.onChange(+event.target.value)}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="price.pricePerUser.value"
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    slotProps={{ htmlInput: { 'aria-label': 'price-per-user' }, input: { endAdornment: price?.pricePerUser.currency } }}
                    label="Price per user"
                    onChange={(event) => field.onChange(+event.target.value)}
                  />
                )}
              />
            </Stack>
            <Stack>
              {showAllowedUsersField && (
                <Controller
                  control={form.control}
                  name="settings.limit.allowedUsers"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      slotProps={{ htmlInput: { 'aria-label': 'allowed-users' } }}
                      label="Allowed users"
                      onChange={(event) => field.onChange(+event.target.value)}
                      error={!!form.formState.errors.settings}
                      helperText={form.formState.errors.settings ? 'Allowed users is required' : undefined}
                    />
                  )}
                />
              )}
            </Stack>
          </Loader>
        </Stack>
      </Stack>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
        }}
      >
        <Button
          loading={isPatching}
          type="submit"
          onClick={form.handleSubmit(handleSubmit)}
          variant="contained"
          size="medium"
          disabled={!form.formState.isDirty}
        >
          Save
        </Button>
        <Button
          loading={isDeleting}
          type="submit"
          onClick={handleDeleteOverride}
          variant="text"
          color="error"
          size="medium"
          disabled={!isOverriden}
        >
          Delete overrides
        </Button>
      </Stack>
    </BorderedStack>
  )
}

const Skeleton = (props: React.ComponentProps<typeof _Skeleton>) => {
  return <_Skeleton height={56} {...props} sx={{ ...props.sx, transform: 'none' }} />
}

export const CustomerUserSettingsSkeleton = () => {
  return (
    <Stack
      data-testid="customer-user-settings-skeleton"
      sx={{
        gap: 2,
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'stretch',
          gap: 2,
          flex: 1,
        }}
      >
        <Skeleton height={48} width="100%" />
        <Skeleton height={48} width="100%" />
      </Stack>
      <Skeleton height={48} width="100%" />
    </Stack>
  )
}
